import { useCallback, useEffect, useRef } from 'react';

import { mapPascalCaseQuoteOptions } from './use-freight-quote-progress';

import type { RawFreightQuote } from './use-freight-quote-progress';
import type { PusherFallbackOptions, PusherServiceInterface } from './use-label-batch-progress';
import type { LtlQuote } from '../../types/ltl';
import type { MutableRefObject } from 'react';

type FulfillmentPlanQuoteMessageData = {
  ProductTransactionId: string;
  FulfillmentPlanId: number;
  Quote: RawFreightQuote | null;
  QuoteIndex: number;
  TotalQuotes: number;
  IsComplete: boolean;
  IsSuccess: boolean | null;
  Errors: { Code?: string; Message: string }[] | null;
  errorMessage?: string;
};

export type FreightFulfillmentPlanQuotePusherMessage = {
  messageGuid: string;
  data: FulfillmentPlanQuoteMessageData;
  uniqueMessageId: string;
};

export type UseFreightFulfillmentPlanQuoteProgressOptions = {
  pusherService: PusherServiceInterface;
  pendingGuidsRef: MutableRefObject<Set<string>>;
  onQuotes: (quotes: LtlQuote[]) => void;
  onError: (message: string, code?: string | null) => void;
  onComplete: () => void;
  timeoutMs?: number;
};

export type UseFreightFulfillmentPlanQuoteProgressReturn = {
  startListening: (jobGuids: string[]) => void;
  cancelListening: () => void;
};

const TIMEOUT_MESSAGE = 'Quote request timed out. Please try again.';
const DEFAULT_TIMEOUT_MS = 30000;

export const useFreightFulfillmentPlanQuoteProgress = ({
  pusherService,
  pendingGuidsRef,
  onQuotes,
  onError,
  onComplete,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseFreightFulfillmentPlanQuoteProgressOptions): UseFreightFulfillmentPlanQuoteProgressReturn => {
  const onQuotesRef = useRef(onQuotes);
  onQuotesRef.current = onQuotes;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const handleMessage = useCallback((message: FreightFulfillmentPlanQuotePusherMessage) => {
    const { messageGuid, data } = message;

    if (!pendingGuidsRef.current.has(messageGuid)) return;

    if (data.errorMessage) {
      onErrorRef.current(data.errorMessage);
    }

    if (data.Quote) {
      onQuotesRef.current([mapPascalCaseQuoteOptions(data.Quote)]);
    }

    if (data.IsComplete) {
      pendingGuidsRef.current.delete(messageGuid);

      if (data.Errors?.length) {
        onErrorRef.current(data.Errors[0].Message, data.Errors[0].Code);
      }

      if (pendingGuidsRef.current.size === 0) {
        onCompleteRef.current();
      }
    }
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fallbackOptions: PusherFallbackOptions = {
      timeoutMs,
      onTimeout: () => {
        if (pendingGuidsRef.current.size === 0) return;
        pendingGuidsRef.current.clear();
        onErrorRef.current(TIMEOUT_MESSAGE);
        onCompleteRef.current();
      },
    };

    const unsubscribe = pusherService.subscribe<FreightFulfillmentPlanQuotePusherMessage>(
      'freight-quote-fulfillment-plan',
      handleMessage,
      fallbackOptions,
    );

    return unsubscribe;
    // pendingGuidsRef is a stable ref — .current is always fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusherService, timeoutMs, handleMessage]);

  const startListening = useCallback((jobGuids: string[]) => {
    pendingGuidsRef.current.clear();
    jobGuids.forEach((guid) => pendingGuidsRef.current.add(guid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelListening = useCallback(() => {
    pendingGuidsRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { startListening, cancelListening };
};
